/**
 * AI Advisory Page (P4) — port of approved mockup v0.3 (2026-07-06).
 *
 * ONE ranked queue from S.aiCopilot: top 3 recommendations render as stacked
 * "Next Best Moves" hero cards, the rest as slim expand-on-demand rows.
 * Impact evidence runs as a moving tape. English-only UI: quotes display
 * evidenceQuoteEn with a "See original" reveal of the verbatim source text.
 *
 * Read-only over existing stores (recommendations/impacts/lastScan) — this is
 * presentation; the P5 phase wires real confirm-gated action handlers. Until
 * then Act routes to the relevant page after an explicit uiConfirm.
 */

import { S, save } from '../core/state.js';
import { esc, safeUrl, uid, fByCur, isLatinReadable } from '../core/utils.js';
import { riskScore } from '../core/risk-calc.js';
import { dismissRecommendation, runCopilotScan, isCopilotScanning } from './ai-copilot.js';
import { openExit } from './trades.js';
import { lg } from './history.js';
import { uiConfirm } from '../ui/dialog.js';
import { toast } from '../ui/layout.js';
import { renderTrustStrip } from './alpha-ledger-page.js';
import { classifyMandate } from '../core/alpha-score.js';

/** Accumulate-mandate badge for rec cards — visible context on every surface (2026-07-22). */
function _mandateBadge(rec) {
  if (!rec || !rec.ticker) return '';
  const ctx = { holdings: S.holdings || [], portfolios: S.portfolios || [], trades: S.trades || [], prices: S.prices || {}, fxRate: S.fxRate };
  if (classifyMandate(rec.ticker, new Date().toISOString(), ctx) !== 'accumulate') return '';
  return `<span class="adv-mandate" title="5-10y monthly accumulation plan — exit calls don't apply and are never scored">ACCUMULATE</span>`;
}
import { renderOnly } from '../app/render-loop.js';
import { ledgerRecActed } from '../core/decision-ledger.js';

let _lens = 'all';

const HERO_ACCENTS = { stop_loss: 'var(--red)', take_profit: 'var(--grn)', sell: 'var(--ora)', alert: 'var(--yel)', rebalance: 'var(--vio, #a78bfa)', dca_adjust: 'var(--grn)', buy: 'var(--grn)', scale_in: 'var(--grn)', hold: 'var(--cyan)', hedge: 'var(--ora)' };
const HERO_ICONS = { stop_loss: '⛑', take_profit: '◎', sell: '▼', alert: '⚖', rebalance: '⇄', dca_adjust: '◆', buy: '▲', scale_in: '▲', hold: '◆', hedge: '⇄' };
/** Where the Act button routes until P5 wires real handlers. */
const ACT_TARGET = { stop_loss: 'trades', take_profit: 'trades', sell: 'portfolio', dca_adjust: 'monthly', buy: 'portfolio', scale_in: 'trades', rebalance: 'portfolio', alert: 'signals', hold: 'portfolio', hedge: 'trades' };

// ─── LENS CLASSIFICATION ────────────────────────────────────────

function _lensOf(rec) {
  const lenses = [];
  if (rec.type === 'alert' && /conflict/i.test(rec.title || '')) lenses.push('conf');
  if (['dca_adjust', 'buy', 'scale_in'].includes(rec.type)) lenses.push('opp');
  if (rec.ticker) lenses.push('pos');
  if (!lenses.length) lenses.push('pos');
  return lenses;
}

function _activeRecs() {
  const recs = (S.aiCopilot && S.aiCopilot.recommendations || []).filter(r => !r.dismissed && !r.acted);
  return _lens === 'all' ? recs : recs.filter(r => _lensOf(r).includes(_lens));
}

/** Highest-confidence surfaced impact for a ticker (evidence for hero/row). */
function _impactFor(ticker) {
  if (!ticker) return null;
  const all = (S.aiCopilot && S.aiCopilot.impacts) || [];
  return all.filter(i => i.surfaced && i.tickers.includes(ticker))
    .sort((a, b) => b.confidence - a.confidence)[0] || null;
}

// ─── TEMPLATES ──────────────────────────────────────────────────

function _quoteBlock(im, id) {
  if (!im) return '';
  // Last line of defence, and the one that matters today: impacts ALREADY stored carry whatever
  // the model returned, and they are never re-ingested. A stored Mandarin "English copy" must
  // not reach the page just because it was written before the ingest guard existed.
  const enRaw = isLatinReadable(im.evidenceQuoteEn) ? im.evidenceQuoteEn : im.evidenceQuote;
  const en = esc(enRaw);
  const orig = esc(im.evidenceQuote);
  const differs = enRaw !== im.evidenceQuote;
  const link = im.newsUrl ? ` · <a href="${esc(safeUrl(im.newsUrl))}" target="_blank" rel="noopener">source ↗</a>` : '';
  return `<div class="adv-q" id="advq-${esc(id)}">"${en}"
    ${differs ? `<button class="adv-seeorig" data-action="advSeeOrig" data-args='["${esc(id)}"]'>See original ▾</button><span class="adv-orig">"${orig}"</span>` : ''}
    <br><span class="adv-qsrc">— ${esc(im.source)} · conf ${im.confidence} · ${esc(im.magnitude)}${link}</span></div>`;
}

function _heroCard(rec, idx) {
  const ac = HERO_ACCENTS[rec.type] || 'var(--cyan)';
  const im = _impactFor(rec.ticker);
  return `<div class="adv-hero" style="--hac:${ac}" id="advhero-${esc(rec.id)}">
    <div class="adv-htop"><div class="adv-hic">${HERO_ICONS[rec.type] || '◆'}</div>
      <div class="adv-htitle">${esc(rec.title || rec.type)}</div>${_mandateBadge(rec)}<span class="adv-hrank">#${idx + 1}</span></div>
    <div class="adv-hsub">${esc(rec.reasoning || '')}</div>
    ${_quoteBlock(im, rec.id)}
    <div class="adv-hbtns">
      <button class="adv-hgo" data-action="advAct" data-args='["${esc(rec.id)}"]'>${esc(rec.action || 'Review')} →</button>
      <button class="adv-hskip" data-action="advSelfActed" data-args='["${esc(rec.id)}"]' title="Mark as followed — you executed this yourself (broker, portfolio edit, etc.). Feeds the followed-vs-ignored scorecard.">✓ I did this</button>
      <button class="adv-hskip" data-action="advDismiss" data-args='["${esc(rec.id)}"]'>Not now</button>
      <span class="adv-hwhy">${rec.engine ? 'ENGINE · deterministic' : 'AI · scan'}${rec.urgency ? ' · ' + esc(rec.urgency.toUpperCase()) : ''}</span>
    </div></div>`;
}

function _queueRow(rec) {
  const ac = HERO_ACCENTS[rec.type] || 'var(--cyan)';
  const im = _impactFor(rec.ticker);
  // An absent metric is not a metric. BTC/ETH/XRP fall outside the 15-ticker TA cap, so
  // their rsi/trend arrive null and this rendered a literal "rsi null · trend null" chip
  // to the user — a missing value dressed as a value. (audit 2026-08-01)
  const metrics = Object.entries(rec.metrics || {})
    .filter(([, v]) => v != null && v !== '' && v !== 'null' && !(typeof v === 'number' && !isFinite(v)))
    .slice(0, 4)
    .map(([k, v]) => `<span>${esc(k)} ${esc(String(v))}</span>`).join('');
  return `<div class="adv-row" style="--ac:${ac}" id="advrow-${esc(rec.id)}">
    <div class="adv-rmain" data-action="advToggle" data-args='["${esc(rec.id)}"]'>
      <span class="adv-dot"></span><span class="adv-rtk">${esc(rec.ticker || '—')}</span>
      <span class="adv-rt">${esc(rec.title || '')}</span>${_mandateBadge(rec)}
      <span class="adv-rhint">${rec.engine ? 'ENGINE' : 'AI'}</span>
      <button class="adv-rgo" data-action="advAct" data-args='["${esc(rec.id)}"]'>${esc(_shortBtn(rec))}</button>
      <span class="adv-chev">▾</span>
    </div>
    <div class="adv-rdetail"><div class="adv-rinner">
      ${esc(rec.reasoning || '')}${rec.action ? `<br><b>${esc(rec.action)}</b>` : ''}
      ${_quoteBlock(im, rec.id + '-r')}
      ${metrics ? `<div class="adv-ms">${metrics}</div>` : ''}
      <button class="adv-dis" data-action="advSelfActed" data-args='["${esc(rec.id)}"]' title="Mark as followed — you executed this yourself. Feeds the followed-vs-ignored scorecard." style="color:var(--grn)">✓ I did this</button>
      <button class="adv-dis" data-action="advDismiss" data-args='["${esc(rec.id)}"]'>✕ Dismiss this recommendation</button>
    </div></div></div>`;
}

function _shortBtn(rec) {
  return { stop_loss: 'Set SL', take_profit: 'Apply TP', sell: 'Trim', dca_adjust: 'Plan DCA', buy: 'Buy', scale_in: 'Scale in', rebalance: 'Rebalance', alert: 'Set Alert', hedge: 'Hedge', hold: 'Review' }[rec.type] || 'Review';
}

// ─── RENDER ─────────────────────────────────────────────────────

export function renderAdvisory() {
  const page = document.getElementById('page-advisory');
  if (!page) return;
  try { renderTrustStrip(); } catch (_e) { /* strip is non-critical */ }
  const $ = id => document.getElementById(id);

  // meta line
  const last = S.aiCopilot && S.aiCopilot.lastScan;
  const li = S.aiCopilot && S.aiCopilot.lastImpact;
  let risk = null;
  try { risk = riskScore(); } catch (_e) { /* optional */ }
  const ago = last ? _ago(last.ts) : 'never';
  // impact age is trust-critical: stale evidence must LOOK stale (audit W1-2)
  const impAgeMs = li && li.ts ? Date.now() - Date.parse(li.ts) : null;
  const impStale = impAgeMs != null && impAgeMs > 6 * 60 * 60 * 1000;
  const impAgo = li && li.ts ? _ago(li.ts) : null;
  // A stale timestamp and a FAILING stage look identical from the age alone. When the
  // stage recorded a reason, say so out loud rather than letting the user infer it.
  const impErr = li && li.error ? li.error : null;
  $('advMeta').innerHTML =
    `scan ${esc(ago)}${li ? ` · ${li.analyzed} news → ${li.kept} impacts ${impStale ? `<b style="color:var(--yel)">⚠ ${esc(impAgo)}</b>` : `(${esc(impAgo)})`}` : ''}` +
    `${impErr ? ` · <b style="color:var(--red)">impact stage failing: ${esc(impErr)}</b>` : ''}` +
    `${risk ? ` · risk <b class="adv-risk-${risk.band.toLowerCase()}">${esc(risk.band.toUpperCase())} ${risk.overall}</b>` : ''}`;

  // evidence tape (all impacts incl. below-gate — watch items are context)
  const imps = (S.aiCopilot && S.aiCopilot.impacts) || [];
  const tape = imps.slice(0, 14).map(im => {
    const cls = im.direction === 'bullish' ? 'up' : im.direction === 'bearish' ? 'dn' : 'un';
    const _q = isLatinReadable(im.evidenceQuoteEn) ? im.evidenceQuoteEn : im.evidenceQuote;
    return `<span class="adv-ti"><b>${esc(im.tickers.join(','))}</b> <span class="${cls}">${esc(im.direction)}·${esc(im.magnitude)} ${im.confidence}</span> <q>"${esc(_q)}"</q></span>`;
  });
  const tapeEl = $('advTape');
  if (tape.length) {
    tapeEl.innerHTML = [...tape, ...tape].join('');
    tapeEl.parentElement.style.display = '';
    // Mockup pace = 9s per item (5 items / 46s). Deliberately NOT pixel-measured:
    // this render often runs while the page is hidden (scan refresh), where
    // scrollWidth is 0 and any measurement silently falls back to a too-fast
    // default. Per-item duration is deterministic and matches the dashboard glide.
    tapeEl.style.animationDuration = Math.max(46, tape.length * 9) + 's';
  } else { tapeEl.innerHTML = ''; tapeEl.parentElement.style.display = 'none'; }

  // heroes + queue
  const recs = _activeRecs();
  $('advHeroes').innerHTML = recs.slice(0, 3).map(_heroCard).join('')
    || '<div class="adv-empty">No recommendations right now — "no signal" is a valid answer. Run a scan or come back when the market gives us something real.</div>';
  $('advQueue').innerHTML = recs.slice(3).map(_queueRow).join('');

  // lens counts + active state
  const all = (S.aiCopilot && S.aiCopilot.recommendations || []).filter(r => !r.dismissed);
  const counts = { all: all.length, pos: 0, opp: 0, conf: 0 };
  all.forEach(r => _lensOf(r).forEach(l => { counts[l] = (counts[l] || 0) + 1; }));
  document.querySelectorAll('#advLens button').forEach(b => {
    const l = b.dataset.lens;
    b.classList.toggle('on', l === _lens);
    const c = b.querySelector('.adv-cnt');
    if (c) c.textContent = counts[l] ?? 0;
  });
}

function _ago(iso) {
  if (!iso) return 'never';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  return h < 24 ? h + 'h ago' : Math.floor(h / 24) + 'd ago';
}

// ─── ACTIONS (registered in main.js) ────────────────────────────

export function advLens(lens) {
  _lens = lens || 'all';
  renderAdvisory();
}

export function advToggle(id) {
  const row = document.getElementById('advrow-' + id);
  if (row) row.classList.toggle('open');
}

export function advSeeOrig(id) {
  const q = document.getElementById('advq-' + id);
  if (q) q.classList.toggle('showorig');
}

/**
 * Dismiss with a WHY (2026-07-22, his ask): accumulate-mandate recs auto-record
 * 'mandate_mismatch' (the reason is knowable — zero extra clicks, his ASELS case);
 * active-lane recs get a two-choice inline picker (disagree = conscious rejection,
 * the highest-value ignored signal · later = deferral). Reason rides the ledger's
 * reasoning column on the rec_dismissed row.
 */
export function advDismiss(id, reason) {
  const rec = (S.aiCopilot?.recommendations || []).find(r => r.id === id && !r.dismissed);
  if (!rec) return;
  if (!reason && rec.ticker) {
    const ctx = { holdings: S.holdings || [], portfolios: S.portfolios || [], trades: S.trades || [], prices: S.prices || {}, fxRate: S.fxRate };
    if (classifyMandate(rec.ticker, new Date().toISOString(), ctx) === 'accumulate') reason = 'mandate_mismatch';
  }
  if (!reason) { _showDismissReasons(id); return; }
  dismissRecommendation(id, reason);
  toast(reason === 'mandate_mismatch' ? 'Dismissed — policy: accumulate asset' : reason === 'disagree' ? 'Dismissed — you disagree (recorded)' : 'Dismissed — later', 'a-b');
  renderAdvisory();
}

function _showDismissReasons(id) {
  const host = document.getElementById('advhero-' + id) || document.getElementById('advrow-' + id);
  if (!host) { dismissRecommendation(id, 'later'); renderAdvisory(); return; }
  if (host.querySelector('.adv-whydis')) return;
  const d = document.createElement('div');
  d.className = 'adv-whydis';
  d.innerHTML = `<span>Why dismiss?</span>
    <button data-action="advDismiss" data-args='["${esc(id)}","disagree"]'>I disagree</button>
    <button data-action="advDismiss" data-args='["${esc(id)}","later"]'>Later / not now</button>
    <button data-action="advWhyDisCancel" data-args='["${esc(id)}"]'>✕</button>`;
  host.appendChild(d);
}

export function advWhyDisCancel(id) {
  const host = document.getElementById('advhero-' + id) || document.getElementById('advrow-' + id);
  host?.querySelector('.adv-whydis')?.remove();
}

/** Open, non-TD trade for a ticker (first match). */
function _findOpenTrade(ticker) {
  return (S.trades || []).find(t => t.ticker === ticker && t.status !== 'Closed' && t.type !== 'TimeDeposit') || null;
}

/** Mark a rec done: it drops from heroes/queue/Moves; kept in the array for audit.
 *  tradeId (optional) links the rec to the trade it acted on in the decision ledger. */
function _markActed(rec, note, tradeId, reason) {
  rec.acted = true;
  rec.actedAt = new Date().toISOString();
  ledgerRecActed(rec, { trade_id: tradeId || null, reason: reason || null });
  save();
  lg('advisory', rec.ticker || '—', note || `${rec.type} executed via AI Advisory`);
  _doneFlash(rec.id);
}

/** "✓ I did this" — self-reported follow (broker-side or manual execution).
 *  Feeds the followed arm of the Alpha Ledger; price is stamped at this moment. */
export function advSelfActed(id) {
  const rec = (S.aiCopilot?.recommendations || []).find(r => r.id === id && !r.acted);
  if (!rec) return;
  _markActed(rec, `Self-reported: executed outside the app (${rec.type}${rec.ticker ? ' · ' + rec.ticker : ''})`, null, 'self_reported');
  renderOnly('dash', 'advisory');
}

/** Apply-TP acted is recorded on exit CONFIRM, not dialog open (over-count fix 2026-07-22).
 *  trades.js calls window.__advisoryExitHook(tradeId) after a successful exit save. */
let _pendingTpAct = null;
export function _setPendingTpAct(recId, tradeId) { _pendingTpAct = { recId, tradeId, ts: Date.now() }; }
if (typeof window !== 'undefined') {
  window.__advisoryExitHook = (tradeId) => {
    if (!_pendingTpAct || _pendingTpAct.tradeId !== tradeId) return;
    if (Date.now() - _pendingTpAct.ts > 15 * 60 * 1000) { _pendingTpAct = null; return; } // stale intent
    const rec = (S.aiCopilot?.recommendations || []).find(r => r.id === _pendingTpAct.recId && !r.acted);
    _pendingTpAct = null;
    if (rec) _markActed(rec, `TP exit CONFIRMED (${rec.ticker})`, tradeId);
  };
}

/** Brief ✓ Done flash on the card/row, then re-render (the rec is gone). */
function _doneFlash(id) {
  const el = document.getElementById('advrow-' + id) || document.getElementById('advhero-' + id);
  if (el) {
    el.classList.add('acted');
    setTimeout(() => { renderOnly('advisory', 'dash'); }, 700);
  } else {
    renderOnly('advisory', 'dash');
  }
}

export async function advAct(id) {
  const rec = (S.aiCopilot && S.aiCopilot.recommendations || []).find(r => r.id === id);
  if (!rec) return;
  const title = rec.ticker ? `${rec.ticker} — ${_shortBtn(rec)}` : _shortBtn(rec);

  // ── SET SL: fully executed here (writes to the trade ledger) ──
  if (rec.type === 'stop_loss') {
    const t = _findOpenTrade(rec.ticker);
    if (!t) { toast('No matching open trade found', 'a-r'); return; }
    const sl = rec.metrics && rec.metrics.suggestedSL;
    if (!sl) { toast('No suggested SL on this recommendation', 'a-r'); return; }
    const ccy = t.currency || 'USD';
    const ok = await uiConfirm(
      `Set stop loss on the open ${t.side} ${esc(t.ticker)} at ${fByCur(ccy, sl)}?\n\nWrites to your ledger only — nothing executes at a broker.`,
      { title, okText: 'Set SL' }
    );
    if (!ok) return;
    t.sl = sl;
    t._updatedAt = Date.now();
    _markActed(rec, `SL set to ${sl} (${ccy}) on open ${t.side}`, t.id);
    toast(`✓ Done — ${t.ticker} SL set at ${fByCur(ccy, sl)}`, 'a-g');
    return;
  }

  // ── APPLY TP: hand off to the existing exit flow (it owns the ledger math) ──
  if (rec.type === 'take_profit') {
    const t = _findOpenTrade(rec.ticker);
    if (!t) { toast('No matching open trade found', 'a-r'); return; }
    const ok = await uiConfirm(
      `${rec.action || 'Apply TP'}\n\nOpens the exit dialog for ${esc(t.ticker)} — quantity and price are confirmed there, then realized P&L and cash are booked.`,
      { title, okText: 'Open exit' }
    );
    if (!ok) return;
    _setPendingTpAct(rec.id, t.id); // acted lands only when the exit dialog is CONFIRMED (window.__advisoryExitHook)
    const nav = window._navActivateTab;
    if (typeof nav === 'function') nav('trades');
    openExit(t.id);
    return;
  }

  // ── REBALANCE: existing review flow ──
  if (rec.type === 'rebalance') {
    const ok = await uiConfirm('Open the Rebalance review? Suggestions are prepared for your approval — nothing moves without it.', { title, okText: 'Open review' });
    if (!ok) return;
    // Opening the review is not a follow — acted lands only when the user applies suggestions
    // (or presses "I did this"). Counting the open here inflated the Followed counter (2026-07-24).
    const nav = window._navActivateTab;
    if (typeof nav === 'function') nav('portfolio');
    // main.js exposes all actions on window (intentional cross-module bridge)
    if (typeof window.startRebalanceReview === 'function') window.startRebalanceReview();
    return;
  }

  // ── SET ALERT: watch entry into the copilot alert stream ──
  if (rec.type === 'alert') {
    const ok = await uiConfirm(`Add a watch alert for ${esc(rec.ticker || 'portfolio')}? It appears in the AI side panel and clears when dismissed.`, { title, okText: 'Set alert' });
    if (!ok) return;
    if (!S.aiCopilot.alerts) S.aiCopilot.alerts = [];
    S.aiCopilot.alerts.unshift({
      id: uid(), type: 'advisory_watch', ticker: rec.ticker || '',
      severity: 'info', message: `[watch] ${rec.title}`, createdAt: new Date().toISOString(), dismissed: false,
    });
    S.aiCopilot.alerts = S.aiCopilot.alerts.slice(0, 12);
    _markActed(rec, `Watch alert set${rec.ticker ? ' for ' + rec.ticker : ''}`);
    toast('✓ Done — watch alert set', 'a-g');
    return;
  }

  // ── Everything else (trim/DCA/buy…): confirm → route; execution stays manual there ──
  const ok = await uiConfirm(
    `${rec.action || rec.title}\n\nThis routes you to the ${ACT_TARGET[rec.type] || 'portfolio'} page to execute manually. Nothing is written automatically and nothing executes at a broker.`,
    { title, okText: 'Continue' }
  );
  if (!ok) return;
  const nav = window._navActivateTab;
  if (typeof nav === 'function') nav(ACT_TARGET[rec.type] || 'portfolio');
}

export async function advScan() {
  if (isCopilotScanning()) { toast('Scan already in progress...', 'a-y'); return; }
  const btn = document.querySelector('#page-advisory .adv-scanbtn');
  const orig = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.style.opacity = '.6'; btn.textContent = 'Scanning…'; }
  try {
    const res = await runCopilotScan({ force: false });
    if (res && res.ok) renderAdvisory();
  } finally {
    if (btn) { btn.disabled = false; btn.style.opacity = ''; btn.textContent = orig || '⟳ Scan'; }
  }
}
